import React, { useContext, useEffect, useRef, useState } from 'react';
import { Container, Grid } from '@mantine/core';
import 'react/jsx-runtime';
import CallStatusChart from './components/CallStatusChart';
import CallHistoryTable from './components/CallHistoryTable';
import CallStat from './components/CallStat';
import FilterResultsSection from 'components/FilterResultsSection';
import CallSentiments from './components/CallSentiments';
import DateRangeFilter from 'components/DateRangeFilter';
import { UpgradePopover } from 'components/upgrade';
import AuthContext from 'context/AuthContext';
import BillingContext from 'context/BillingContext';
import { AccessLevel } from 'interfaces/GetUserResult';
import SEO from 'components/SEO';

import '@mantine/charts/styles.css';
import { DateRange } from 'components/DateRangeFilter/Dropdown';
import ReportContext from 'context/ReportContext';
import { getPercentage } from 'utils/number';
import { formatSecondsToTime } from 'utils/date';
import dayjs from 'dayjs';
import { DATE_RANGE_TIME_FORMAT } from 'constants/reporting';

const ReportingPage = () => {
  const {
    data: { callStats, dateRange, isLoadingCallStats },
    actions: { getCallStats, setDateRange },
  } = useContext(ReportContext);
  const {
    data: { account },
  } = useContext(AuthContext);
  const {
    actions: { setPricingOpened },
  } = useContext(BillingContext);

  const isPremium = account?.access_level === AccessLevel.PRO;
  const [showUpgradePopover, setShowUpgradePopover] = useState(!isPremium);
  const upgradeContainerRef = useRef<HTMLDivElement>(null);

  const fetchCallStats = () => {
    if (!dateRange) {
      return;
    }

    getCallStats({
      start_date: dayjs(dateRange?.from).format(DATE_RANGE_TIME_FORMAT),
      end_date: dayjs(dateRange?.to).format(DATE_RANGE_TIME_FORMAT),
    });
  };

  const applyFilter = (range: DateRange) => {
    setDateRange(range);
  };

  // Close popover when clicking outside
  useEffect(() => {
    if (!showUpgradePopover) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      if (upgradeContainerRef.current && 
          !upgradeContainerRef.current.contains(event.target as Node) && 
          !document.querySelector('.mantine-Popover-dropdown')?.contains(event.target as Node)) {
        setShowUpgradePopover(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUpgradePopover]);

  useEffect(() => {
    if (isPremium) {
      fetchCallStats();
    } else {
      // Trigger analytics event for paywall
      if (typeof window !== 'undefined' && (window as any).analytics && typeof (window as any).analytics.track === 'function') {
        (window as any).analytics.track('reporting_paywall_shown');
      }
    }
    // Intentionally excluding analytics tracking functions from dependencies as they are part of
    // conditional paywall display logic that should only run when date range or premium status changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, isPremium]);

  const handleUpgrade = () => {
    // Close popover
    setShowUpgradePopover(false);
    
    // Open pricing modal using the BillingContext
    setPricingOpened(true);
    
    // Track upgrade button click
    if (typeof window !== 'undefined' && (window as any).analytics && typeof (window as any).analytics.track === 'function') {
      (window as any).analytics.track('reporting_upgrade_button_clicked');
    }
  };

  const callStatsOptions = [
    { label: 'Total no. of calls', value: callStats.num_call },
    {
      label: 'Average call duration',
      value: formatSecondsToTime(callStats.avg_call_duration),
    },
    { label: 'No. of messages taken', value: callStats.num_message },
    {
      label: 'Calls resolved',
      value: callStats.num_complete,
      labelDescriptor: 'by Reception',
      valueDescriptor: `${getPercentage(callStats.num_complete, callStats.num_call)}% of Total calls`,
    },
  ];

  if (!isPremium) {
    return (
      <>
        <SEO 
          title="Call Reporting | Nucleus AI"
          description="View comprehensive call analytics, transcripts, and performance metrics for your AI receptionist."
        />
        <div 
          ref={upgradeContainerRef}
          style={{ 
            minHeight: '60vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          <UpgradePopover
            opened={showUpgradePopover}
            onClose={() => setShowUpgradePopover(false)}
            upgradeTitle="Unlock Reporting & Analytics"
            upgradeDescription="Use AI to maximize customer insights. Unlock full call details, transcripts, recordings, and analytics to analyze every conversation effortlessly. Upgrade to Pro to access reporting."
            onUpgrade={handleUpgrade}
          >
            <div style={{ width: '100%', height: '100%' }}></div>
          </UpgradePopover>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO 
        title="Call Reporting | Nucleus AI"
        description="View comprehensive call analytics, transcripts, and performance metrics for your AI receptionist."
      />
      <Container fluid p={0} mb={{ base: 'sm', md: 'md' }}>
        <Grid>
          <Grid.Col span={{ base: 12, md: 6, xl: 3 }}>
            <DateRangeFilter onApply={applyFilter} defaultCustomValue="30d" disableTime={false} />
          </Grid.Col>
        </Grid>
      </Container>
      <FilterResultsSection>
        <Grid gutter={{ base: 'sm', md: 'md' }}>
          {callStatsOptions.map((stat, index) => (
            <Grid.Col key={index} span={{ base: 12, sm: 6, lg: 3 }}>
              <CallStat
                key={stat.label}
                stat={stat}
                isLoading={isLoadingCallStats}
              />
            </Grid.Col>
          ))}

          <Grid.Col span={{ base: 12, md: 6 }}>
            <CallSentiments />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <CallStatusChart />
          </Grid.Col>

          <Grid.Col span={12}>
            <CallHistoryTable />
          </Grid.Col>
        </Grid>
      </FilterResultsSection>
    </>
  );
};

export default ReportingPage;
